/**
 * @file OrderLegalTermsCard.tsx
 * @description Карточка «Принятые условия» на вкладке «Обзор» заказа
 * (phase19.16, партия «Юридические документы», шаг Б; SB143;
 * uiux-legal-steps-b-c §2): какие редакции оферты, политики ПДн и условий
 * возврата действовали при оформлении на сайте — справка для спора.
 *
 * Строка документа целиком — ссылка ↗ на страницу редакции на сайте
 * (`<сайт>/legal/<адрес>/revisions/<N>`, новая вкладка): сайт, а не
 * кабинет — у менеджера заказов может не быть права настроек. Название — из
 * снимка, переносится, а не обрезается. Перенесённая по дате редакция
 * помечена «· по дате заказа*» со сноской. Строка «Согласие на обработку
 * ПДн» ведёт в карточку клиента → «Согласия» (право `SHOP_VIEW_CUSTOMERS`).
 * На телефоне строки друг под другом по 48 px, на ПК — «Документ / Редакция».
 * Состояния — `orderLegalTermsView`.
 */

import type { LegalAcceptanceDto } from "@chaiberi/shared";
import { ChevronRight, ExternalLink } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { useHasPermission } from "../../auth/hooks";
import { isAbsoluteHref, siteHref, useShopSiteUrl } from "../../hooks/useShopSiteUrl";
import type { OrderDto } from "../../lib/api-orders";
import { orderLegalTermsView, revisionLine } from "../../lib/order-legal-terms";
import { formatShopDate, formatShopDateTime } from "../../lib/shop-time";
import { EditorialCard } from "../ui/EditorialCard";
import { SectionLabel } from "../ui/SectionLabel";
import { cn } from "@/lib/utils";

interface Props {
  order: Pick<OrderDto, "source" | "createdAt" | "legalAcceptances" | "legalAcceptanceSince" | "customerId" | "pdConsent">;
  shopSlug?: string;
}

const ROW = "grid min-h-12 grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 py-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_auto]";

/** Строка: документ, редакция и значок перехода; с адресом — ссылка на сайт. */
function TermRow({ title, detail, href, internal }: { title: string; detail: ReactNode; href: string | null; internal?: boolean }) {
  const body = (
    <>
      <span className="type-body break-words font-medium text-on-surface">{title}</span>
      <span className="type-caption col-start-1 text-on-surface-variant lg:col-start-2 lg:row-start-1">{detail}</span>
      {href ? (
        internal ? (
          <ChevronRight aria-hidden className="col-start-2 row-span-2 row-start-1 size-4 text-on-surface-variant lg:col-start-3 lg:row-span-1" />
        ) : (
          <ExternalLink aria-hidden className="col-start-2 row-span-2 row-start-1 size-4 text-on-surface-variant lg:col-start-3 lg:row-span-1" />
        )
      ) : null}
    </>
  );
  if (!href) return <li className={ROW}>{body}</li>;
  return (
    <li>
      {internal ? (
        <Link to={href} className={cn(ROW, "rounded-md hover:bg-surface-low")}>{body}</Link>
      ) : (
        <a href={href} target="_blank" rel="noopener noreferrer" className={cn(ROW, "rounded-md hover:bg-surface-low")}>
          {body}
          <span className="sr-only"> (откроется на сайте в новой вкладке)</span>
        </a>
      )}
    </li>
  );
}

export function OrderLegalTermsCard({ order, shopSlug }: Props) {
  // Сервер отдаёт абсолютные адреса редакций; адрес сайта — запас для относительных.
  const relative = [...(order.legalAcceptances ?? []).map((r) => r.href), order.pdConsent?.legalRevision?.href ?? ""].some((h) => h !== "" && !isAbsoluteHref(h));
  const siteUrl = useShopSiteUrl(relative);
  const canViewCustomers = useHasPermission("SHOP_VIEW_CUSTOMERS");
  const view = orderLegalTermsView(order);
  if (view.kind === "hidden") return null;

  const consentHref = shopSlug && canViewCustomers ? `/shop/${shopSlug}/customers/${order.customerId}?tab=profile#consents` : null;
  const pd = order.pdConsent?.legalRevision ?? null;
  const row = (r: LegalAcceptanceDto) => (
    <TermRow key={r.role} title={r.title} detail={`${revisionLine(r)}${r.backfilled ? " · по дате заказа*" : ""}`} href={siteHref(siteUrl, r.href)} />
  );

  return (
    <EditorialCard>
      <EditorialCard.Body className="flex flex-col gap-2">
        <SectionLabel as="h2">Принятые условия</SectionLabel>
        {view.kind !== "rows" ? (
          <p className="type-body text-on-surface-variant">{view.text}</p>
        ) : (
          <>
            <p className="type-caption text-on-surface-variant">При оформлении на сайте {formatShopDateTime(order.createdAt)}</p>
            <ul className="flex flex-col divide-y divide-[var(--border)]">
              {view.rows.map(row)}
              {view.missing.map((m) => (
                <li key={m.role} className={cn("type-body py-2", m.tone === "attention" ? "text-warning" : "text-on-surface-variant")}>
                  {m.text}
                </li>
              ))}
              {pd ? (
                <TermRow title="Согласие на обработку ПДн" detail={`ред. ${pd.number} от ${formatShopDate(pd.publishedAt)}${pd.byDate ? " · по дате согласия" : ""}`} href={siteHref(siteUrl, pd.href)} />
              ) : null}
              <TermRow
                title={pd ? "Все согласия клиента" : "Согласие на обработку ПДн"}
                detail={consentHref ? "Смотрите в карточке клиента → Согласия" : "Хранится в карточке клиента"}
                href={consentHref}
                internal
              />
            </ul>
            {view.footnote ? <p className="type-caption text-on-surface-variant">* {view.footnote}</p> : null}
          </>
        )}
      </EditorialCard.Body>
    </EditorialCard>
  );
}
